import { apiClient } from '@/api/client';
import {
  PathsAdminUsersUserIdPlansGetParametersQueryFilter,
  Plan,
} from '@/api/models';
import { queryKeys } from '@/lib/queryKeys';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

export type PlanFilterType = PathsAdminUsersUserIdPlansGetParametersQueryFilter;
export const PlanFilter = PathsAdminUsersUserIdPlansGetParametersQueryFilter;

export interface UserPlansFilters {
  page: number;
  limit: number;
  filter?: PlanFilterType;
  sortBy: 'startAt' | 'endAt' | 'createdAt';
  sortOrder: 'asc' | 'desc';
}

export interface UserPlansState {
  plans: Plan[];
  total: number;
  totalPages: number;
  isLoading: boolean;
  filters: UserPlansFilters;
}

export interface UserPlansActions {
  setPage: (page: number) => void;
  setFilter: (filter: PlanFilterType | undefined) => void;
  setSortBy: (sortBy: UserPlansFilters['sortBy']) => void;
  setSortOrder: (sortOrder: 'asc' | 'desc') => void;
  resetFilters: () => void;
}

const defaultFilters: UserPlansFilters = {
  page: 1,
  limit: 10,
  filter: undefined as PlanFilterType | undefined,
  sortBy: 'startAt',
  sortOrder: 'desc',
};

export const useUserPlans = (userId: number | undefined) => {
  const [filters, setFilters] =
    React.useState<UserPlansFilters>(defaultFilters);

  // Reset filters when userId changes
  React.useEffect(() => {
    setFilters(defaultFilters);
  }, [userId]);

  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.ADMIN_USER_PLANS, userId, filters],
    queryFn: async () => {
      if (!userId) return { plans: [], total: 0, totalPages: 0 };

      const response = await apiClient.GET('/admin/users/{userId}/plans', {
        params: {
          path: { userId },
          query: {
            page: filters.page,
            limit: filters.limit,
            filter: filters.filter,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
          },
        },
      });

      if (response.error) {
        throw new Error('Failed to fetch user plans');
      }

      // Get pagination info from headers
      const total = parseInt(
        response.response.headers.get('X-Total-Count') || '0',
      );
      const totalPages = parseInt(
        response.response.headers.get('X-Total-Pages') || '0',
      );

      return {
        plans: response.data || [],
        total,
        totalPages,
      };
    },
    enabled: !!userId,
  });

  const setPage = React.useCallback((page: number) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  const setFilter = React.useCallback((filter: PlanFilterType | undefined) => {
    setFilters(prev => ({ ...prev, filter, page: 1 }));
  }, []);

  const setSortBy = React.useCallback((sortBy: UserPlansFilters['sortBy']) => {
    setFilters(prev => ({ ...prev, sortBy, page: 1 }));
  }, []);

  const setSortOrder = React.useCallback((sortOrder: 'asc' | 'desc') => {
    setFilters(prev => ({ ...prev, sortOrder, page: 1 }));
  }, []);

  const resetFilters = React.useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const state: UserPlansState = {
    plans: data?.plans || [],
    total: data?.total || 0,
    totalPages: data?.totalPages || 0,
    isLoading,
    filters,
  };

  const actions: UserPlansActions = {
    setPage,
    setFilter,
    setSortBy,
    setSortOrder,
    resetFilters,
  };

  return { state, actions };
};
